import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { IconCheck, IconClose, IconRefresh } from "../icons";

/**
 * APROVAR AGENDAMENTO — a aba do GESTOR OPERACIONAL.
 *
 * O Planejamento monta o checkpoint da atividade (datas, equipe, dias, veículo,
 * documentos) e, ao "Pré-agendar as 3" ou "Confirmar c/ cliente", manda para cá
 * em vez de ocupar a agenda. Quem responde pela operação do dia é o gestor: é a
 * APROVAÇÃO dele que marca os dias na agenda dos colaboradores.
 *
 * A tela mostra tudo o que ele precisa para decidir sem abrir outro sistema:
 * cliente, serviço, quantos dias (viagem × execução), veículo, documentos,
 * quem vai e em que datas.
 */

interface Membro {
  id: number;
  nome?: string;
}

interface Opcao {
  inicio?: string;
  fim?: string;
  rotulo?: string;
  dias_uteis: string[];
  equipe: Membro[];
}

interface Pedido {
  id: number;
  atividade_id: number;
  tipo: "pre" | "confirmada";
  indice: number | null;
  opcoes: Opcao[];
  status: "Pendente" | "Aprovada" | "Reprovada";
  solicitado_por: string | null;
  solicitado_em: string;
  avaliado_por: string | null;
  avaliado_em: string | null;
  motivo: string | null;
  atividade_nome: string | null;
  cliente: string | null;
  data_cliente: string | null;
  tipo_servico: string | null;
  descricao_servico: string | null;
  dias_execucao: number | null;
  dias_viagem: number | null;
  dias_servico: number | null;
  veiculo: string | null;
  qtd_colaboradores: number | null;
  documentos: string | null;
  ensaios_eletricos: string | null;
  ponto_energia: string | null;
  necessario_escada: string | null;
  dias_semana: string | null;
}

/** "2026-08-05" -> "05/08". */
function dia(s: string): string {
  const d = String(s).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : d;
}

/** "2026-08-05" -> "05/08/2026". */
function dataCompleta(s: string | null): string {
  if (!s) return "—";
  const d = String(s).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d.split("-").reverse().join("/") : d;
}

function quando(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

export default function AprovarAgendamentoView() {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<number | null>(null);
  const [verHistorico, setVerHistorico] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setPedidos(await api.aprovacoesAgenda.list());
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const pendentes = useMemo(() => pedidos.filter((p) => p.status === "Pendente"), [pedidos]);
  const decididos = useMemo(() => pedidos.filter((p) => p.status !== "Pendente"), [pedidos]);

  async function aprovar(p: Pedido) {
    const datas = p.opcoes.flatMap((o) => o.dias_uteis).length;
    if (
      !confirm(
        `Aprovar o agendamento de "${p.cliente || p.atividade_nome}"?\n\n` +
          `A agenda da equipe será marcada (${datas} dia(s) no total).`,
      )
    ) {
      return;
    }
    setOcupado(p.id);
    try {
      const r = await api.aprovacoesAgenda.aprovar(p.id);
      setAviso(r.mensagem);
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível aprovar.");
    } finally {
      setOcupado(null);
    }
  }

  async function reprovar(p: Pedido) {
    const motivo = prompt(
      `Reprovar o agendamento de "${p.cliente || p.atividade_nome}".\n\n` +
        "Escreva o motivo (o Planejamento precisa saber o que ajustar):",
    );
    if (motivo === null) return;
    if (!motivo.trim()) {
      alert("Escreva o motivo da reprovação.");
      return;
    }
    setOcupado(p.id);
    try {
      const r = await api.aprovacoesAgenda.reprovar(p.id, motivo.trim());
      setAviso(r.mensagem);
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível reprovar.");
    } finally {
      setOcupado(null);
    }
  }

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;

  const lista = verHistorico ? decididos : pendentes;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div>
          <span className="badge">{pendentes.length} aguardando aprovação</span>{" "}
          {decididos.length > 0 && <span className="badge">{decididos.length} já decidido(s)</span>}
        </div>
        <div className="toolbar-right">
          <button className="btn-secondary" onClick={() => setVerHistorico((v) => !v)}>
            {verHistorico ? "Ver pendentes" : "Ver histórico"}
          </button>
          <button className="btn-secondary" onClick={carregar} disabled={loading}>
            <IconRefresh size={16} /> Atualizar
          </button>
        </div>
      </div>

      {aviso && (
        <div className="aprov-aviso">
          {aviso}
          <button className="btn-secondary btn-sm" onClick={() => setAviso(null)}>
            Fechar
          </button>
        </div>
      )}

      {loading ? (
        <div className="empty">Carregando...</div>
      ) : lista.length === 0 ? (
        <div className="empty">
          {verHistorico
            ? "Nenhum agendamento decidido ainda."
            : "Nenhum agendamento aguardando aprovação. Quando o Planejamento pré-agendar ou confirmar uma data com o cliente, o pedido aparece aqui."}
        </div>
      ) : (
        <div className="aprov-lista">
          {lista.map((p) => (
            <article key={p.id} className={`aprov-card ${p.status !== "Pendente" ? "decidido" : ""}`}>
              <header className="aprov-head">
                <div>
                  <h3>{p.cliente || p.atividade_nome || `Atividade #${p.atividade_id}`}</h3>
                  <p className="aprov-sub">
                    {p.tipo === "pre" ? (
                      <>Pré-agendamento das 3 datas — aguardando o cliente escolher</>
                    ) : (
                      <>
                        Data confirmada com o cliente
                        {p.data_cliente ? <> — início {dataCompleta(p.data_cliente)}</> : null}
                      </>
                    )}
                  </p>
                </div>
                <span
                  className={`pill ${
                    p.status === "Aprovada" ? "pill-ok" : p.status === "Reprovada" ? "pill-danger" : "pill-warn"
                  }`}
                >
                  {p.status}
                </span>
              </header>

              <div className="aprov-dados">
                <div>
                  <span>Serviço</span>
                  <strong>{p.tipo_servico || p.atividade_nome || "—"}</strong>
                </div>
                <div>
                  <span>Dias</span>
                  <strong>
                    {p.dias_execucao ?? "—"}
                    {p.dias_viagem != null && p.dias_servico != null && (
                      <small className="muted"> ({p.dias_viagem} viagem + {p.dias_servico} serviço)</small>
                    )}
                  </strong>
                </div>
                <div>
                  <span>Equipe</span>
                  <strong>{p.qtd_colaboradores ?? p.opcoes[0]?.equipe?.length ?? "—"}</strong>
                </div>
                <div>
                  <span>Veículo</span>
                  <strong>{p.veiculo || "—"}</strong>
                </div>
                <div>
                  <span>Regime</span>
                  <strong>{p.dias_semana || "—"}</strong>
                </div>
                <div>
                  <span>Ensaios elétricos</span>
                  <strong>{p.ensaios_eletricos || "—"}</strong>
                </div>
                <div>
                  <span>Ponto de energia</span>
                  <strong>{p.ponto_energia || "—"}</strong>
                </div>
                <div>
                  <span>Escada</span>
                  <strong>{p.necessario_escada || "—"}</strong>
                </div>
              </div>

              {p.documentos && (
                <p className="aprov-doc">
                  <strong>Documentos:</strong> {p.documentos}
                </p>
              )}
              {p.descricao_servico && <p className="aprov-escopo">{p.descricao_servico}</p>}

              <div className="aprov-opcoes">
                {p.opcoes.map((op, i) => (
                  <div key={i} className="aprov-opcao">
                    <div className="aprov-opcao-head">
                      <strong>
                        {p.tipo === "pre" ? `Opção ${i + 1}` : "Data confirmada"}
                        {op.inicio && op.fim ? (
                          <> · {dataCompleta(op.inicio)} a {dataCompleta(op.fim)}</>
                        ) : null}
                      </strong>
                      <span className="muted">{op.dias_uteis?.length ?? 0} dia(s)</span>
                    </div>
                    <div className="aprov-dias">
                      {(op.dias_uteis ?? []).map((d) => (
                        <span key={d} className="aprov-dia">
                          {dia(d)}
                        </span>
                      ))}
                    </div>
                    <div className="aprov-equipe">
                      {(op.equipe ?? []).map((m, idx) => (
                        <span key={m.id} className="aprov-membro">
                          {m.nome ?? `#${m.id}`}
                          {idx === 0 && <small className="muted"> (responsável)</small>}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <footer className="aprov-foot">
                <span className="muted">
                  Enviado por {p.solicitado_por || "Planejamento"} em {quando(p.solicitado_em)}
                  {p.status !== "Pendente" && (
                    <>
                      {" · "}
                      {p.status} por {p.avaliado_por || "—"} em {quando(p.avaliado_em)}
                      {p.motivo ? ` — ${p.motivo}` : ""}
                    </>
                  )}
                </span>
                {p.status === "Pendente" && (
                  <span className="aprov-acoes">
                    <button
                      className="btn-secondary btn-danger-text"
                      disabled={ocupado === p.id}
                      onClick={() => reprovar(p)}
                    >
                      <IconClose size={15} /> Reprovar
                    </button>
                    <button className="btn-primary" disabled={ocupado === p.id} onClick={() => aprovar(p)}>
                      <IconCheck size={15} /> {ocupado === p.id ? "Aprovando…" : "Aprovar e marcar na agenda"}
                    </button>
                  </span>
                )}
              </footer>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
